import { useEffect, useState } from "react";
import client from "../api/client";
import { formatCurrency } from "../utils/format";
import { TrendingUp, CalendarCheck, Layers, Loader2 } from "lucide-react";

function PageHeader({ title, subtitle, children }) {
  return (
    <div className="h-16 border-b border-border px-8 flex items-center justify-between sticky top-0 bg-background/80 backdrop-blur-md z-10">
      <div>
        <h1 className="font-heading text-xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    client.get("/dashboard").then((res) => setData(res.data));
  }, []);

  if (!data)
    return (
      <>
        <PageHeader title="Dashboard" />
        <div className="p-8 text-muted-foreground flex items-center gap-2 text-sm">
          <Loader2 className="h-4 w-4 animate-spin" /> Načítání…
        </div>
      </>
    );

  const cards = [
    { label: "Celkový výnos", value: formatCurrency(data.total_revenue), icon: TrendingUp },
    { label: "Workshopy tento měsíc", value: data.month_count, icon: CalendarCheck },
    { label: "Workshopy celkem", value: data.total_count, icon: Layers },
  ];

  return (
    <>
      <PageHeader title="Dashboard" subtitle="Přehled výkonu firmy" />
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="summary-cards">
          {cards.map((c) => (
            <div key={c.label} className="border border-border rounded-lg p-5 bg-card">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs uppercase tracking-widest text-muted-foreground">{c.label}</span>
                <c.icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className="font-heading text-3xl font-semibold tracking-tight">{c.value}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-border font-medium text-sm">Měsíční přehled</div>
            <table className="w-full text-sm" data-testid="monthly-table">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="text-left font-medium px-5 py-2">Měsíc</th>
                  <th className="text-right font-medium px-5 py-2">Workshopy</th>
                  <th className="text-right font-medium px-5 py-2">Výnos</th>
                </tr>
              </thead>
              <tbody>
                {data.monthly.map((m) => (
                  <tr key={m.month} className="border-b border-border/60 last:border-0">
                    <td className="px-5 py-2.5">{m.month_display}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{m.count}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums font-medium">{formatCurrency(m.revenue)}</td>
                  </tr>
                ))}
                {data.monthly.length === 0 && (
                  <tr><td colSpan={3} className="px-5 py-8 text-center text-muted-foreground">Zatím žádná data</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="border border-border rounded-lg overflow-hidden">
            <div className="px-5 py-3 border-b border-border font-medium text-sm">Top prodejci</div>
            <table className="w-full text-sm" data-testid="top-sellers">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-muted-foreground border-b border-border">
                  <th className="text-left font-medium px-5 py-2">Prodejce</th>
                  <th className="text-right font-medium px-5 py-2">Workshopy</th>
                  <th className="text-right font-medium px-5 py-2">Výnos</th>
                </tr>
              </thead>
              <tbody>
                {data.top_sellers.map((s) => (
                  <tr key={s.name} className="border-b border-border/60 last:border-0">
                    <td className="px-5 py-2.5">{s.name}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{s.count}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums font-medium">{formatCurrency(s.revenue)}</td>
                  </tr>
                ))}
                {data.top_sellers.length === 0 && (
                  <tr><td colSpan={3} className="px-5 py-8 text-center text-muted-foreground">Zatím žádná data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
